import EventEmitter from 'eventemitter3';

class WallPan extends EventEmitter {

    // TODO: add panel size + offset to constructor
    /**
     * @constructor
     * @param {Node} panel Wall panel dom element
     * @param {Node} overlay to animate over wall
     * @param {{x: Number, y: Number}} Wall offset position
     */
    constructor({
        panel,
        overlay,
        offset
    } = {}) {
        super();

        this.dom = {
            panel : panel,
            overlay: overlay
        };
        this.offset = offset;

        // drag
        this.dragTreshold = 0.8;
        this.dragDuration = 2;

        this.pos = {x:0,y:0}
        this.targetPos = {x:0,y:0}
        this.lastPos = {x: 0, y: 0};
        this.distance = 0;
        this.lastDistance = 0;
        this.easing = 0.6;
        this.scale = 1;
        this.panTarget = null;
        this.isDragging = false;
        this.isAnimating = false;
        this.canUpdateGrid = false;
        this.isSimplePan = false;

        this.init();
    }

    /**
     * Is cursor is grabbing style
     * @type {Boolean}
     */
    get cursorGrabbing() {
        return this.dom.panel.classList.contains('cursor-grabbing');
    }

    set cursorGrabbing(v) {
        this.dom.panel.classList[v ? 'add' : 'remove']('cursor-grabbing');
    }

    // Note : Not very perf on edge and internet explorer
    get simplifiedVersion() {
        return ENV.browser === 'internet explorer' || ENV.browser === 'edge';
    }

    /**
     * Init wall pan
     */
    init() {
        this._initPan();
        this._addEvents();

        if(this.simplifiedVersion) {
            this.isSimplePan = true;
            this.easing = 0.9;
        }
    }

    /**
     * Init pan
     * @private
     */
    _initPan() {
        // create a manager for that element
        this.mc = new Hammer.Manager(this.dom.panel);

        // create a recognizer
        this.pan = new Hammer.Pan({ direction: Hammer.DIRECTION_ALL, threshold: 0 })

        // add the recognizer
        this.mc.add(this.pan);
    }

    /**
     * Handle pan event, drag start, move & end
     * @param {Object} e Pan Event returned object
     * @private
     */
    _handlePan = (e) => {

        // ref to our object
        const panel = this.dom.panel;
        const bounds = panel.getBoundingClientRect();

        // DRAG STARTED
        // let's get current pos
        if( ! this.isDragging ) {
            this.panTarget = e.target;
            this.isDragging = true;
            this.lastPos.x = bounds.left - this.offset.x;
            this.lastPos.y = bounds.top - this.offset.y;
            this.cursorGrabbing = true;
            this.emit('drag:start', e);
            this.setStatus('drag:start');
            this._startTranslate();
        }

        // we determine where the x,y of this
        // object is relative to where it's "last" known position is
        // NOTE:
        //    deltaX and deltaY are cumulative
        // Thus we need to always calculate 'real x and y' relative
        // to the "lastPosX/Y"
        this.targetPos.x = e.deltaX * this.dragTreshold + this.lastPos.x;
        this.targetPos.y = e.deltaY * this.dragTreshold + this.lastPos.y;

        // where we forget we are dragging
        if (e.isFinal) {
            this.isDragging = false;
            this.setStatus('drag:end');
            this.once('translate:end', () => {
                this._stopTranslate();
                this.cursorGrabbing = false;
            });
        }
    };

    /**
     * Animate panel translate
     * @private
     */
    _translatePanel = () => {

        // reset rafId
        this.rafId = undefined;

        // set animate true
        this.isAnimating = true;

        // ease translate values
        this.pos.x += (this.targetPos.x - this.pos.x) * this.easing;
        this.pos.y += (this.targetPos.y - this.pos.y) * this.easing;

        // set panel transform
        if(this.isSimplePan) {
            this._translateTo(this.pos); //, this.scale);
        } else {
            // get dist
            this.posDiffX = this.targetPos.x - this.pos.x;
            this.posDiffY = this.targetPos.y - this.pos.y;
            this.distance = Math.sqrt(this.posDiffX * this.posDiffX + this.posDiffY * this.posDiffY);

            // get scale
            this.scale += (this.distance * 0.001 - this.lastDistance * 0.001) * this.easing * 0.4;

            this._translateTo(this.pos, this.scale);
            // set overlay transform
            this.dom.overlay.style.opacity = this.scale - 1;
        }

        this.lastDistance = this.distance;
        const crossTranslateDiff = Math.round( this.pos.x + this.pos.y + this.targetPos.x + this.targetPos.y  );
        const crossTranslate = Math.round( this.pos.x + this.pos.y ) * 2;
        this._startTranslate();

        // if drag and translate animation ended
        if(!this.isDragging && Math.abs(crossTranslate - crossTranslateDiff) < 2) {
            this.emit('translate:end', {target: this.panTarget});
        }
    };

    /**
     * Start translating
     * @private
     */
    _startTranslate() {
        if(!this.rafId) {
            this.rafId = requestAnimationFrame(this._translatePanel);
        }
    }

    /**
     * Stop translating
     * @private
     */
    _stopTranslate() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = undefined;
        }
    }

    /**
     * Set translate & scale values on dom panel
     * @param {{x: Number, y: Number}} position
     * @param {Number} scale
     * @private
     */
    _translateTo(position, scale = 1) {
        const transform = `translate3d(${position.x * scale}px, ${position.y * scale}px, 0) scale3d(${scale}, ${scale}, 1)`;
        this.dom.panel.style.transform = transform;
        this.dom.panel.style.oTransform = transform;
        this.dom.panel.style.msTransform = transform;
        this.dom.panel.style.mozTransform = transform;
        this.dom.panel.style.webkitTransform = transform;
    }

    /**
     * Add wall events
     * @private
     */
    _addEvents() {
        this.mc.on('pan', this._handlePan);
    }

    /**
     * Add formated and colored console log for loggin wall status
     * @param {String} msg Message
     */
    setStatus(msg) {
        //console.log('%c-- WallPan - ' + msg, "color:#009688");
    }
}

export {WallPan as default}


// WEBPACK FOOTER //
// ./src/js/pages/recipes/wall/WallManager/wallPan.js